import React, { useEffect } from "react";
import styled from "styled-components";
import { useReveal } from "../hooks/useReveal";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import PhotoSlot from "../components/PhotoSlot";
import { Section, Container, Panel, Eyebrow, H2, Lead, CenteredHead } from "../components/ui";
import { CoachiiStats } from "../sections/Origin";
import FinalCta from "../sections/FinalCta";
import { photos } from "../content/photos";

const Wrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
  background-color: var(--paper);
  color: var(--text-hi);
`;

const MissionSection = styled(Section)`
  padding-top: 6.5rem;

  @media screen and (max-width: 48em) {
    padding-top: 5.5rem;
  }
`;

const Mission = styled.h1`
  font-family: var(--fontL);
  font-weight: 500;
  font-size: clamp(2.25rem, 5vw, 4rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  max-width: 18ch;
  margin-bottom: 1.5rem;
  text-wrap: balance;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media screen and (max-width: 64em) {
    gap: 3rem;
  }

  @media screen and (max-width: 48em) {
    display: flex;
    flex-direction: column-reverse;
    gap: 2rem;
  }
`;

const Story = styled.div`
  p + p {
    margin-top: 1rem;
  }

  @media screen and (max-width: 48em) {
    text-align: center;
  }
`;

const Quote = styled.blockquote`
  padding: 1.125rem 1.25rem;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 16px 40px rgba(10, 10, 11, 0.14), 0 0 0 1px rgba(10, 10, 11, 0.04);
  font-family: var(--fontL);
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.01em;

  footer {
    margin-top: 0.5rem;
    font-family: var(--fontR);
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: 0;
    color: var(--text-lo);
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media screen and (max-width: 48em) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  padding: 1.75rem 1.5rem;
  border-radius: 20px;
  background: #ffffff;

  span {
    display: block;
    font-family: var(--fontL);
    font-size: 0.875rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: #0a7a53;
    margin-bottom: 1.25rem;
  }

  h3 {
    font-family: var(--fontL);
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: -0.015em;
    margin-bottom: 0.625rem;
  }

  p {
    font-family: var(--fontR);
    font-size: 0.9375rem;
    line-height: 1.55;
    color: var(--text-lo);
  }
`;

const Founders = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  max-width: 920px;
  margin: 0 auto;

  @media screen and (max-width: 48em) {
    grid-template-columns: 1fr;
  }
`;

// No portrait: each founder is introduced by what he brings to the product
const Founder = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem 1.75rem;
  border-radius: 20px;
  background: var(--paper-2);

  span {
    font-family: var(--fontL);
    font-size: 0.875rem;
    font-weight: 500;
    color: #0a7a53;
    margin-bottom: 1.25rem;
  }

  h3 {
    font-family: var(--fontL);
    font-size: 1.75rem;
    font-weight: 500;
    letter-spacing: -0.02em;
  }

  small {
    display: block;
    margin: 0.25rem 0 1rem;
    font-family: var(--fontR);
    font-size: 0.875rem;
    color: var(--text-lo);
  }

  p {
    font-family: var(--fontR);
    font-size: 0.9375rem;
    line-height: 1.6;
    color: var(--text-lo);
  }

  footer {
    margin-top: auto;
    padding-top: 1.5rem;
    font-family: var(--fontR);
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--text-hi);

    &::before {
      content: "";
      display: block;
      height: 1px;
      margin-bottom: 1rem;
      background: rgba(10, 10, 11, 0.08);
    }
  }
`;

const About = () => {
  const revealRef = useReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <Wrapper ref={revealRef}>
      <Header />
      <main>
        <MissionSection>
          <Panel>
            <CenteredHead style={{ marginBottom: 0 }}>
              <Eyebrow $color="#0a7a53">Notre mission</Eyebrow>
              <Mission>Rendre l'argent facile, pour tout le monde.</Mission>
              <Lead style={{ maxWidth: "38rem" }}>
                On nous apprend à travailler pour gagner de l'argent, jamais à le gérer. Nea existe pour que comprendre
                ses finances ne soit plus un privilège : tu vois clair, tu décides, et quand tu doutes, tu poses la
                question.
              </Lead>
            </CenteredHead>
          </Panel>
        </MissionSection>

        <Section style={{ paddingTop: "4.5rem", paddingBottom: "4.5rem" }}>
          <Container>
            <Row data-reveal>
              <PhotoSlot photo={photos.story} side="right">
                <Quote>
                  « Le coaching en tête-à-tête a ses limites. J'ai voulu le digitaliser pour aider un maximum de
                  monde. »
                  <footer>Michael, cofondateur</footer>
                </Quote>
              </PhotoSlot>
              <Story>
                <Eyebrow>Notre histoire</Eyebrow>
                <H2>Tout a commencé par du coaching.</H2>
                <Lead as="div">
                  <p>
                    Avant Nea, il y a Coachii : Michael accompagne des particuliers, en tête-à-tête, pour reprendre la
                    main sur leur budget et commencer à investir. Ça marche. Mais le constat revient à chaque fois :
                    un vrai accompagnement, seuls ceux qui en ont les moyens y ont accès.
                  </p>
                  <p>
                    Alors il décide de digitaliser sa méthode. Alexandre, développeur passionné d'IA, le rejoint pour
                    lui donner vie : tes comptes réunis, tes dépenses classées, et un copilote à qui tu peux parler
                    comme à un coach.
                  </p>
                </Lead>
              </Story>
            </Row>
          </Container>
        </Section>

        <Section style={{ paddingTop: "1rem", paddingBottom: "4.5rem" }}>
          <Container>
            <CenteredHead data-reveal>
              <Eyebrow>Du terrain, pas de la théorie</Eyebrow>
              <H2>Des vraies personnes, de vrais résultats.</H2>
            </CenteredHead>
            <CoachiiStats />
          </Container>
        </Section>

        <Section>
          <Panel data-reveal>
            <CenteredHead>
              <Eyebrow>Le constat</Eyebrow>
              <H2>L'argent est partout. Personne ne nous apprend à le gérer.</H2>
              <Lead style={{ maxWidth: "38rem" }}>
                Et comme le sujet reste tabou, on garde tout pour soi. Résultat : on perd le fil, on se frustre, on
                remet à plus tard.
              </Lead>
            </CenteredHead>
            <Cards>
              <Card>
                <h3>On se sent seul face à ses choix</h3>
                <p>Personne à qui demander si on épargne assez, ou si on s'y prend bien.</p>
              </Card>
              <Card>
                <h3>On doute de chaque décision</h3>
                <p>Trop d'avis, trop de jargon, et jamais de réponse adaptée à sa propre situation.</p>
              </Card>
              <Card>
                <h3>On culpabilise de ne pas mieux faire</h3>
                <p>Alors on évite de regarder ses comptes, et le problème grossit tout seul.</p>
              </Card>
            </Cards>
          </Panel>
        </Section>

        <Section style={{ paddingTop: "5rem", paddingBottom: "5rem" }}>
          <Container>
            <CenteredHead data-reveal>
              <Eyebrow>Nos principes</Eyebrow>
              <H2>Trois règles qu'on ne négocie pas.</H2>
            </CenteredHead>
            <Cards>
              <Card data-reveal style={{ "--reveal-i": 0, background: "var(--paper-2)" }}>
                <span>01</span>
                <h3>Comprendre, pas obéir</h3>
                <p>
                  La meilleure gestion financière, c'est celle que tu comprends vraiment. Nea explique toujours son
                  raisonnement.
                </p>
              </Card>
              <Card data-reveal style={{ "--reveal-i": 1, background: "var(--paper-2)" }}>
                <span>02</span>
                <h3>Ta sécurité avant les produits</h3>
                <p>
                  On commence par ton coussin de sécurité et ton budget. L'investissement vient après, quand la base
                  est solide.
                </p>
              </Card>
              <Card data-reveal style={{ "--reveal-i": 2, background: "var(--paper-2)" }}>
                <span>03</span>
                <h3>Zéro conflit d'intérêts</h3>
                <p>
                  Pas de commission sur des placements, pas de revente de données. Nea n'a rien à te vendre d'autre
                  qu'elle-même.
                </p>
              </Card>
            </Cards>
          </Container>
        </Section>

        <Section style={{ paddingBottom: "5rem" }}>
          <Container>
            <CenteredHead data-reveal>
              <Eyebrow>L'équipe</Eyebrow>
              <H2>Deux fondateurs, une même idée.</H2>
              <Lead style={{ maxWidth: "36rem" }}>
                On ne vient pas de la finance classique. On vient du terrain : la pédagogie, la technologie et
                l'humain.
              </Lead>
            </CenteredHead>
            <Founders>
              <Founder data-reveal style={{ "--reveal-i": 0 }}>
                <span>La méthode</span>
                <h3>Michael</h3>
                <small>Investisseur et coach en finances personnelles</small>
                <p>
                  A accompagné des dizaines de personnes à reprendre le contrôle de leurs finances. Il a voulu rendre
                  cette expertise accessible à tous, pas seulement à ceux qui peuvent s'offrir un coach.
                </p>
                <footer>Dans nea : la méthode de coaching, le budget et les objectifs.</footer>
              </Founder>
              <Founder data-reveal style={{ "--reveal-i": 1 }}>
                <span>La technologie</span>
                <h3>Alexandre</h3>
                <small>Développeur, passionné d'intelligence artificielle</small>
                <p>
                  S'est reconnu dans le projet dès le premier jour. Il construit l'app et son copilote pour que
                  l'expérience reste fluide, simple et moderne.
                </p>
                <footer>Dans nea : l'app et son copilote IA.</footer>
              </Founder>
            </Founders>
          </Container>
        </Section>

        <FinalCta />
      </main>
      <Footer />
    </Wrapper>
  );
};

export default About;
